import { inject, Injectable } from '@angular/core';
import {
  IBaseElement,
  IBaseElementPayload,
  IConfigurationPayload,
  ISeat,
  ISeatPayload,
  ISeatPriorityPayload,
  ISeatsConnection,
  ISeatsPlan,
  ISectorEditPayload,
  ISectorElement
} from '../../interfaces';
import { ICurve } from '../../interfaces/ICurve';
import { ICurvePoint } from '../../interfaces/ICurvePoint';
import { BaseElementManager, ImageManager, RowManager, SeatManager, SectorManager } from '../../managers';
import { CreatorSteps, GuidWrapper } from '../../types';
import { CreatorCacheService } from './CreatorCacheService';
import { CreatorStateService } from './CreatorStateService';
import { SectorService } from './SectorService';

type StepPayloadMap = {
  'sector-plan': ISectorEditPayload;
  'seats-plan': ISectorEditPayload;
  'connections': ISeatsConnection[];
  'zones': IConfigurationPayload;
  'configuration': null;
  'seat-priority': ISeatPriorityPayload
}

@Injectable({
  providedIn: 'root'
})
export class CreatorCurrentStepService {

  private readonly _sectorService = inject(SectorService);

  public isStepDirty(id: number): boolean {
    switch (CreatorStateService.currentStep) {
      case 'seats-plan':
        return this.isSeatsPlanDirty(id);
      case 'sector-plan':
        return this.isSectorPlanDirty(id);
      case 'connections':
        return this.isConnectionsDirty(id);
      case 'zones':
        return this.isZonesDirty(id);
      case 'seat-priority':
        return this.isSeatsWeightsDirty(id);
    }
  }

  public stepPayload<T extends CreatorSteps>(id: number): StepPayloadMap[T] {
    switch (CreatorStateService.currentStep) {
      case 'seats-plan':
        return this.getSeatsPlanPayload(id) as StepPayloadMap[T];
      case 'sector-plan':
        return this.getSeatsPlanPayload(id) as StepPayloadMap[T];
      case 'connections':
        return this.getConnectionsPayload(id) as StepPayloadMap[T];
      case 'zones':
        return this.getZonesPayload(id) as StepPayloadMap[T];
      case 'seat-priority':
        return this.getSeatsWeightsPayload(id) as StepPayloadMap[T];
    }
  }

  public curvesPayload(id: number): ICurve[] {
    const seatsPlan = CreatorCacheService.seatsPlansCacheMap.get(id)?.seatsPlan ?? CreatorStateService.seatsPlan;
    const rankedSeats = RowManager.formatSeatsToSeatsSeatsWithRanks(seatsPlan.seats);
    
    const curvePointsByRank = new Map<string, { points: ICurvePoint[], rank: number }>();
    
    rankedSeats.forEach(row => {
      if (row.seats.length < 2) return;
      
      const sortedSeats = [...row.seats].sort((a, b) => 
        a.sectorElement.x - b.sectorElement.x
      );
      
      const leftmostSeat = sortedSeats[0];
      const rightmostSeat = sortedSeats[sortedSeats.length - 1];
      
      const rowWidth = (rightmostSeat.sectorElement.x + rightmostSeat.sectorElement.w) - leftmostSeat.sectorElement.x;
      const quarterPoint = rowWidth / 4;
      
      const points = [
        {
          x: Math.round(leftmostSeat.sectorElement.x - 25),
          y: Math.round(leftmostSeat.sectorElement.y + (leftmostSeat.sectorElement.h / 2)),
          pointOrder: 0
        },
        {
          x: Math.round(leftmostSeat.sectorElement.x + quarterPoint),
          y: Math.round(leftmostSeat.sectorElement.y + (leftmostSeat.sectorElement.h / 2)),
          pointOrder: 1
        },
        {
          x: Math.round(leftmostSeat.sectorElement.x + (quarterPoint * 3)),
          y: Math.round(leftmostSeat.sectorElement.y + (leftmostSeat.sectorElement.h / 2)),
          pointOrder: 2
        },
        {
          x: Math.round(rightmostSeat.sectorElement.x + rightmostSeat.sectorElement.w + 25),
          y: Math.round(rightmostSeat.sectorElement.y + (rightmostSeat.sectorElement.h / 2)),
          pointOrder: 3
        }
      ];
      curvePointsByRank.set(row.rankToken, { points, rank: leftmostSeat.rank });
    });
    
    return Array.from(curvePointsByRank.entries()).map(([rankToken, { points, rank }]) => ({
      rank,
      rankToken,
      points,
      seatConfigurationId: seatsPlan.seatConfigurationId
    }));
  }

  public elementsPayload(id: number): { createElements: IBaseElementPayload[], editElements: IBaseElementPayload[] } {
    const seatsPlan = CreatorCacheService.seatsPlansCacheMap.get(id)?.seatsPlan ?? CreatorStateService.seatsPlan;
    const initSeatsPlanState = CreatorCacheService.seatsPlansCacheMap.get(id)?.initState ?? CreatorStateService.initSeatsPlanState;

    const createElements: IBaseElementPayload[] = [];
    const editElements: IBaseElementPayload[] = [];

    for (const baseElement of seatsPlan.baseElements) {
      if (!baseElement) {
        continue;
      }

      if (baseElement.id) {
        const fBaseEl = initSeatsPlanState.baseElements.find(s => s.id === baseElement.id);
        const baseElDataToSend = BaseElementManager.getDataToSend(baseElement);

        if (fBaseEl && this.areBaseElementsDirty(baseElement, fBaseEl)) {
          editElements.push(baseElDataToSend);
        }
      } else {
        createElements.push(BaseElementManager.getDataToSend({
          ...baseElement,
          sectorId: id
        }));
      }
    }

    for (const sector of seatsPlan.sectors) {
      if (!sector) {
        continue;
      }

      /**
       * Sector element has similar structure as base element
       */
      if (sector.id) {
        const fSector = initSeatsPlanState.sectors.find(s => s.id === sector.id);
        const sectorDataToSend = SectorManager.getElementDataToSend(sector);

        if (fSector && this.areSectorsDirty(sector, fSector)) {
          editElements.push(sectorDataToSend);
        }
      } else {
        createElements.push(SectorManager.getElementDataToSend(sector));
      }
    }

    return {createElements, editElements};
  }

  public seatsPayload(id: number): { createSeats: ISeatPayload[], editSeats: ISeatPayload[] } {
    const seatsPlan = CreatorCacheService.seatsPlansCacheMap.get(id)?.seatsPlan ?? CreatorStateService.seatsPlan;
    const initSeatsPlanState = CreatorCacheService.seatsPlansCacheMap.get(id)?.initState ?? CreatorStateService.initSeatsPlanState;
    
    const createSeats: ISeatPayload[] = [];
    const editSeats: ISeatPayload[] = [];

    for (const seat of seatsPlan.seats) {
      if (!seat) {
        continue;
      }

      if (seat.id) {
        const fSeat = initSeatsPlanState.seats.find(s => s.id === seat.id);

        if (fSeat && this.areSeatsDirty(seat, fSeat)) {
          editSeats.push(SeatManager.getDataToSend(seat));
        }
      } else {
        createSeats.push(SeatManager.getDataToSend({
          ...seat,
          sectorElement: {
            ...seat.sectorElement,
            sectorId: id
          }
        }));
      }
    }

    return {createSeats, editSeats};
  }

  public isConnectionsDirty(id: number): boolean {
    const seatsPlan = CreatorCacheService.seatsPlansCacheMap.get(id)?.seatsPlan ?? CreatorStateService.seatsPlan;
    const initSeatsPlanState = CreatorCacheService.seatsPlansCacheMap.get(id)?.initState ?? CreatorStateService.initSeatsPlanState;

    for (const seat of seatsPlan.seats) {
      if (!seat) {
        continue;
      }

      const fSeat = initSeatsPlanState.seats.find(s => s.id === seat.id);

      if (this.compareElements<ISeat>(seat, fSeat).compare('leftSeatDefinitionId').compare('rightSeatDefinitionId').isDirty) {
        return true;
      }
    }

    return false;
  }

  public isZonesDirty(id: number): boolean {
    const seatsPlan = CreatorCacheService.seatsPlansCacheMap.get(id)?.seatsPlan ?? CreatorStateService.seatsPlan;
    const initSeatsPlanState = CreatorCacheService.seatsPlansCacheMap.get(id)?.initState ?? CreatorStateService.initSeatsPlanState;

    for (const seat of seatsPlan.seats) {
      if (!seat) {
        continue;
      }

      const fSeat = initSeatsPlanState.seats.find(s => s.id === seat.id);

      if (this.compareElements<ISeat>(seat, fSeat).compare('areaId').isDirty) {
        return true;
      }
    }

    return false;
  }

  public isSeatsWeightsDirty(id: number): boolean {
    const seatsPlan = CreatorCacheService.seatsPlansCacheMap.get(id)?.seatsPlan ?? CreatorStateService.seatsPlan;
    const initSeatsPlanState = CreatorCacheService.seatsPlansCacheMap.get(id)?.initState ?? CreatorStateService.initSeatsPlanState;
    
    return seatsPlan.seats.some(seat => seat.seatWeight !== initSeatsPlanState.seats.find(s => s.id === seat.id)?.seatWeight);
  }

  public getSeatsWeightsPayload(id: number): ISeatPriorityPayload {
    const seatsPlan = CreatorCacheService.seatsPlansCacheMap.get(id)?.seatsPlan ?? CreatorStateService.seatsPlan;
    const priorityShape = CreatorCacheService.priorityCacheMap.get(id)?.priorityShape ?? CreatorStateService.priorityShape;

    return {
      ...priorityShape,
      sectorId: seatsPlan.id,
      seatsWeights: seatsPlan.seats.map(x => ({
        seatDefinitionId: x.id,
        weight: x.seatWeight
      }))
    }
  }

  public getSeatsPlanPayload(id: number): ISectorEditPayload {
    const seatsPlan = CreatorCacheService.seatsPlansCacheMap.get(id)?.seatsPlan ?? CreatorStateService.seatsPlan;
    this.calculatePositionOfElementsForCashAndBooking(seatsPlan);

    const elementsPayload = this.elementsPayload(id);
    const seatsPayload = this.seatsPayload(id);

    return {
      w: seatsPlan.w,
      h: seatsPlan.h,
      webH: seatsPlan.webH,
      webW: seatsPlan.webW,
      elements: [...elementsPayload.createElements, ...elementsPayload.editElements],
      seats: [...seatsPayload.createSeats, ...seatsPayload.editSeats],
      deleteElementsIds: CreatorCacheService.seatsPlansCacheMap.get(id)?.deleteElementsIds ?? [],
      backgroundColor: seatsPlan.backgroundColor ?? null,
      images: seatsPlan.images.map(ImageManager.getDataToSend) ?? [],
      automaticConnection: !(CreatorStateService.currentStep === 'connections' || CreatorStateService.currentStep === 'zones' || CreatorStateService.currentStep === 'seat-priority'),
      curves: this.curvesPayload(id)
    };
  }

  public getConnectionsPayload(id: number): ISeatsConnection[] {
    const seatsPlan = CreatorCacheService.seatsPlansCacheMap.get(id)?.seatsPlan ?? CreatorStateService.seatsPlan;

    return seatsPlan.seats.map(x => ({
      seatDefinitionId: x.id,
      leftSeatDefinitionId: x.leftSeatDefinitionId,
      rightSeatDefinitionId: x.rightSeatDefinitionId
    }));
  }

  public getZonesPayload(id: number): IConfigurationPayload {
    const seatsPlan = CreatorCacheService.seatsPlansCacheMap.get(id)?.seatsPlan ?? CreatorStateService.seatsPlan;
    const seats: { seatDefinitionId: number, priceAreaId: number }[] = [];

    for (const seat of seatsPlan.seats) {
      if (seat.areaId && seat.id) {
        seats.push({
          seatDefinitionId: seat.id,
          priceAreaId: seat.areaId
        });
      }
    }

    return {
      name: CreatorStateService.currentConfigurationName,
      seats
    };
  }

  private isSectorPlanDirty(id: number): boolean {
    const currentPlan = CreatorStateService.seatsPlan;
    const initPlan = CreatorStateService.initSeatsPlanState;
    let isDirty = false;

    if (currentPlan.sectors.length !== initPlan.sectors.length) {
      isDirty = true;
    } else {
      for (const sector of currentPlan.sectors) {
        const fSector = initPlan.sectors.find(x => x.id === sector.id);

        if (this.compareElements<ISectorElement>(sector, fSector).compare('webX').compare('webY').compare('w').compare('h').compare('rotate').isDirty) {
          isDirty = true;
        }
      }
    }

    return isDirty;
  }

  private isSeatsPlanDirty(id: number): boolean {
    const data = this.stepPayload<'seats-plan'>(id);

    let areImagesDirty = false;
    
    if (CreatorStateService.initSeatsPlanState.images.length !== data.images.length) {
      areImagesDirty = true;
    } else {
      for (const img of data.images) {
        const fInitImage = CreatorStateService.initSeatsPlanState.images.find(x => x.id === img.id);

        if (!fInitImage) {
          areImagesDirty = true;
        } else if (
          img.x !== fInitImage.x
          || img.y !== fInitImage.y
          || img.w !== fInitImage.w
          || img.h !== fInitImage.h
          || img.lock !== fInitImage.lock
          || img.layer !== fInitImage.layer
        ) {
          areImagesDirty = true;
        }
      }
    }

    return !!(data.elements?.length
      || data.deleteElementsIds?.length
      || data.seats?.length
      || areImagesDirty
      || (data.backgroundColor ? data.backgroundColor !== CreatorStateService.initSeatsPlanState.backgroundColor : false));
  }

  private areSectorsDirty(sector1: GuidWrapper<ISectorElement>, sector2: GuidWrapper<ISectorElement>): boolean {
    return this.compareElements<ISectorElement>(sector1, sector2)
      .compare('webX')
      .compare('webY')
      .compare('w')
      .compare('h')
      .compare('ruleTitle')
      .compare('rotate')
      .compare('x')
      .compare('y')
      .isDirty;
  }

  private areSeatsDirty(seat1: GuidWrapper<ISeat>, seat2: GuidWrapper<ISeat>): boolean {
    return this.compareElements<IBaseElement>(seat1.sectorElement, seat2.sectorElement)
        .compare('webX')
        .compare('webY')
        .compare('w')
        .compare('h')
        .compare('ruleTitle')
        .compare('ruleTipText')
        .compare('ruleTopText')
        .compare('ruleBottomText')
        .compare('rotate')
        .compare('flagSeatFac')
        .compare('flagRankFac')
        .compare('shapeId')
        .compare('x')
        .compare('y')
        .isDirty
      || this.compareElements<ISeat>(seat1, seat2)
        .compare('rank')
        .compare('rankToken')
        .compare('seatStatusId')
        .compare('seatTypeId')
        .isDirty;
  }

  private areBaseElementsDirty(el1: GuidWrapper<IBaseElement>, el2: GuidWrapper<IBaseElement>): boolean {
    return this.compareElements<IBaseElement>(el1, el2)
      .compare('webX')
      .compare('webY')
      .compare('w')
      .compare('h')
      .compare('ruleTitle')
      .compare('ruleTipText')
      .compare('ruleTopText')
      .compare('ruleBottomText')
      .compare('rotate')
      .compare('x')
      .compare('y')
      .isDirty;
  }

  /**
   * Elements position for cash and booking should start from 50,50
   */
  private calculatePositionOfElementsForCashAndBooking(seatsPlan: ISeatsPlan): void {
    const seats = seatsPlan.seats;
    const baseElements = seatsPlan.baseElements;
    const sectors = seatsPlan.sectors;

    if (!seats.length && !baseElements.length && !sectors.length) return;

    const allXCoordinates = [
      ...seats.map(seat => seat.sectorElement.webX),
      ...baseElements.filter(el => el).map(el => el.webX),
      ...sectors.filter(sector => sector).map(sector => sector.webX)
    ];

    const allYCoordinates = [
      ...seats.map(seat => seat.sectorElement.webY),
      ...baseElements.filter(el => el).map(el => el.webY),
      ...sectors.filter(sector => sector).map(sector => sector.webY)
    ];

    const minX = Math.min(...allXCoordinates);
    const minY = Math.min(...allYCoordinates);

    const offsetX = minX - 25;
    const offsetY = minY - 25;

    seats.forEach(seat => {
      seat.sectorElement.x = seat.sectorElement.webX - offsetX;
      seat.sectorElement.y = seat.sectorElement.webY - offsetY;
    });

    baseElements.forEach(element => {
      if (element) {
        element.x = element.webX - offsetX;
        element.y = element.webY - offsetY;
      }
    });

    sectors.forEach(sector => {
      if (sector) {
        sector.x = sector.webX - offsetX;
        sector.y = sector.webY - offsetY;
      }
    });

    this._sectorService.recalculateSectorDimensions(true, seatsPlan);
  }

  private compareElements<T>(el1: T, el2: T): {
    compare: (key: keyof T) => { compare: (key: keyof T) => any, readonly isDirty: boolean },
    readonly isDirty: boolean
  } {
    let isDirty = false;

    return {
      compare: function (key: keyof T) {
        if (!isDirty && el1[key] !== el2[key]) {
          isDirty = true;
        }

        return this;
      },
      get isDirty(): boolean {
        return isDirty;
      }
    };
  }
}
